import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  CreditCard, 
  Check, 
  X, 
  Loader2, 
  Crown,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Tag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Preço base do plano PRO
const PRECO_BASE = 19.99;

const SubscriptionManager = ({ cupomAtivo: cupomAtivoProp }) => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const token = localStorage.getItem('lembrou_token');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const [statusRes, perfilRes] = await Promise.all([
        axios.get(`${API}/payments/status`, { headers }),
        axios.get(`${API}/perfil`, { headers }).catch(() => ({ data: null }))
      ]);
      setStatus(statusRes.data);
      setPerfil(perfilRes.data);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
      toast.error('Erro ao carregar informações de pagamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Verificar se voltou do checkout com sucesso
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      toast.success('Pagamento realizado com sucesso! Bem-vindo ao PRO! 🎉');
      // Limpar URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('payment') === 'cancelled') {
      toast.info('Pagamento cancelado');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleUpgrade = async () => {
    try {
      setActionLoading(true);
      const response = await axios.post(`${API}/payments/create-checkout`, {}, { headers });
      
      // Redirecionar para checkout do Stripe
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar pagamento');
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      const response = await axios.post(`${API}/payments/create-portal`, {}, { headers });
      
      // Abrir portal do Stripe em nova aba
      window.open(response.data.portal_url, '_blank');
      setActionLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao abrir portal');
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura? Você ainda terá acesso até o final do período pago.')) {
      return;
    }
    
    try {
      setActionLoading(true);
      const response = await axios.post(`${API}/payments/cancel`, {}, { headers });
      toast.success(response.data.message);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      const response = await axios.post(`${API}/payments/reactivate`, {}, { headers });
      toast.success(response.data.message);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reativar');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  // Calcular preço com desconto - usa prop se disponível, senão busca do perfil
  const cupomAtivo = cupomAtivoProp || perfil?.cupom_ativo;
  const descontoPercent = cupomAtivo?.desconto_percent || 0;
  const precoComDesconto = descontoPercent > 0 
    ? (PRECO_BASE * (1 - descontoPercent / 100)).toFixed(2).replace('.', ',')
    : null;
  const precoBase = PRECO_BASE.toFixed(2).replace('.', ',');

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2">Carregando...</span>
        </div>
      </Card>
    );
  }

  // Usuário FREE
  if (status?.plano === 'FREE') {
    return (
      <Card className="p-6">
        <div className="text-center">
          <Crown className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
          <h3 className="text-xl font-bold mb-2">Upgrade para PRO</h3>
          <p className="text-gray-600 mb-6">
            Desbloqueie recursos ilimitados por apenas
          </p>
          
          {/* Preço com ou sem desconto */}
          <div className="mb-6">
            {descontoPercent > 0 ? (
              <>
                {/* Badge de desconto */}
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-3">
                  <Tag className="w-4 h-4" />
                  {descontoPercent}% OFF com cupom {cupomAtivo.codigo}
                </div>
                
                {/* Preço original riscado */}
                <div className="mb-1">
                  <span className="text-lg text-gray-400 line-through">
                    De: R$ {precoBase}
                  </span>
                </div>
                
                {/* Preço com desconto */}
                <div>
                  <span className="text-sm text-green-600 font-medium">Por: </span>
                  <span className="text-4xl font-bold text-green-600">R$ {precoComDesconto}</span>
                  <span className="text-gray-500">/mês</span>
                </div>
              </>
            ) : (
              <>
                <span className="text-4xl font-bold text-primary">R$ {precoBase}</span>
                <span className="text-gray-500">/mês</span>
              </>
            )}
          </div>

          <ul className="text-left space-y-3 mb-6 max-w-xs mx-auto">
            <li className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              <span>Lembretes ilimitados</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              <span>Importação via CSV/Excel</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              <span>OCR para apólices</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              <span>Relatórios avançados</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              <span>Suporte prioritário</span>
            </li>
          </ul>

          {status?.stripe_enabled ? (
            <Button 
              onClick={handleUpgrade}
              disabled={actionLoading}
              className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl h-12"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Assinar PRO Agora
                </>
              )}
            </Button>
          ) : (
            <p className="text-sm text-gray-500">
              Pagamentos em breve disponíveis
            </p>
          )}
        </div>
      </Card>
    );
  }

  // Usuário PRO
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
            <Crown className="w-6 h-6 text-yellow-600" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Plano PRO</h3>
            <p className="text-sm text-gray-500">
              {descontoPercent > 0 ? (
                <>
                  <span className="line-through text-gray-400 mr-1">R$ {precoBase}</span>
                  <span className="text-green-600 font-medium">R$ {precoComDesconto}/mês</span>
                </>
              ) : (
                `R$ ${precoBase}/mês`
              )}
            </p>
          </div>
        </div>
        
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          status?.subscription_status === 'active' 
            ? 'bg-green-100 text-green-700'
            : status?.subscription_status === 'past_due'
            ? 'bg-yellow-100 text-yellow-700'
            : 'bg-gray-100 text-gray-700'
        }`}>
          {status?.subscription_status === 'active' && 'Ativo'}
          {status?.subscription_status === 'past_due' && 'Pagamento pendente'}
          {status?.subscription_status === 'cancelled' && 'Cancelado'}
          {!status?.subscription_status && 'Ativo'}
        </span>
      </div>

      {/* Alerta de pagamento pendente */}
      {status?.subscription_status === 'past_due' && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Pagamento pendente</p>
              <p className="text-sm text-yellow-700">
                Houve um problema com seu pagamento. Você tem até{' '}
                <strong>{formatDate(status?.grace_period_end)}</strong> para regularizar.
              </p>
              <Button 
                onClick={handleManageSubscription}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                Atualizar forma de pagamento
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Info de cancelamento pendente */}
      {status?.cancel_at_period_end && (
        <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <p className="font-medium text-orange-800">Cancelamento agendado</p>
              <p className="text-sm text-orange-700">
                Sua assinatura será cancelada em{' '}
                <strong>{formatDate(status?.current_period_end)}</strong>.
              </p>
              <Button 
                onClick={handleReactivate}
                disabled={actionLoading}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Manter assinatura
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detalhes da assinatura */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Próxima cobrança</span>
          <span className="font-medium">{formatDate(status?.current_period_end)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Valor</span>
          <span className="font-medium">
            {descontoPercent > 0 ? (
              <>
                <span className="line-through text-gray-400 mr-1">R$ {precoBase}</span>
                <span className="text-green-600">R$ {precoComDesconto}</span>
              </>
            ) : (
              `R$ ${precoBase}`
            )}
          </span>
        </div>
        {descontoPercent > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Cupom aplicado</span>
            <span className="font-medium text-green-600">{cupomAtivo.codigo} ({descontoPercent}% OFF)</span>
          </div>
        )}
      </div>

      {/* Ações */}
      <div className="flex gap-3">
        <Button 
          onClick={handleManageSubscription}
          disabled={actionLoading}
          variant="outline"
          className="flex-1"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Gerenciar Pagamento
        </Button>
        
        {!status?.cancel_at_period_end && (
          <Button 
            onClick={handleCancel}
            disabled={actionLoading}
            variant="ghost"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
        )}
      </div>
    </Card>
  );
};

export default SubscriptionManager;
